import {useCallback, useEffect, useMemo, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {getCemitAuthApiUrl} from '../../apis/cemitApis/apiConfig.ts';
import {
  getStoredTokenOrUndefined,
  mergeContextLog,
  TokenType,
} from '../../appUtils/authentication/authenticationUtils.ts';
import {
  createStoredTokenRefresh,
  isTokenRefreshEnabled,
  extractTokenClaims,
  requestTokenRefresh,
  getTokenSessionStart,
  scheduleTokenRefresh,
  TokenClaims,
  TokenRefreshResult,
} from '../../appUtils/authentication/tokenRefreshUtils.ts';

/**
 * What the login check needs from the token refresh: requestRefresh refreshes and stores
 * a token (requests for the same token share one answer), refreshNow makes the scheduler
 * of the current token refresh at once and retry on failure
 */
export type TokenRefresher = {
  requestRefresh: (token: string) => Promise<TokenRefreshResult>;
  refreshNow: () => void;
};
import {logout} from 'pages/protectedRoute/userUtils.ts';
import {SetTokenType} from 'utils/hooks/useTokenHook.ts';

// This browser's clock minus the auth API's, in ms, measured when a token arrives
const CLOCK_OFFSET_KEY = 'tokenClockOffsetMs';
// When the current session ends, as reported by the auth API: {sessionStart, endsAt}
const SESSION_END_KEY = 'tokenSessionEnd';
// Timers do not run while a laptop sleeps, so overdue timers are looked for this often
const CATCH_UP_INTERVAL_MS = 60 * 1000;

const readClockOffsetMs = (): number => {
  try {
    const offset = Number(window.localStorage.getItem(CLOCK_OFFSET_KEY) ?? NaN);
    return Number.isFinite(offset) ? offset : 0;
  } catch {
    return 0;
  }
};

/**
 * The auth API's current time in ms: this browser's clock, corrected by the offset
 * measured from the last token the auth API issued
 */
export const authApiNowMs = (): number => Date.now() - readClockOffsetMs();

const readSessionEndsAt = (claims: TokenClaims): number | undefined => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(SESSION_END_KEY) ?? 'null');
    return stored?.sessionStart === getTokenSessionStart(claims) &&
      typeof stored.endsAt === 'number'
      ? stored.endsAt
      : undefined;
  } catch {
    return undefined;
  }
};

// The auth API issued the token just now, so its iat is the API's clock, and the
// session ends sessionExpiresIn seconds after that
const saveTokenTimes = (token: string, sessionExpiresIn: number | undefined) => {
  const claims = extractTokenClaims(token);
  if (claims?.iat === undefined) {
    return;
  }

  try {
    const clockOffset = Date.now() - claims.iat * 1000;
    if (process.env.REACT_AUTH_DEBUG && console.log) {
      console.log('saveTokenTimes', {clockOffset, sessionExpiresIn});
    }
    window.localStorage.setItem(CLOCK_OFFSET_KEY, String(clockOffset));
    if (sessionExpiresIn !== undefined) {
      window.localStorage.setItem(
        SESSION_END_KEY,
        JSON.stringify({
          sessionStart: getTokenSessionStart(claims),
          endsAt: claims.iat + sessionExpiresIn,
        }),
      );
    }
  } catch {
    // Without them, times are read on this browser's clock and the session end is guessed
  }
};

/**
 * Runs `callback` while no other tab of this browser runs it, using a Web Lock where the
 * browser has them
 */
const withRefreshLock = (
  callback: () => Promise<TokenRefreshResult>,
): Promise<TokenRefreshResult> => {
  if (navigator.locks) {
    if (process.env.REACT_AUTH_DEBUG && console.log) {
      console.log('withRefreshLock: using navigator.locks');
    }
    return navigator.locks.request('cemit-token-refresh', callback);
  } else {
    if (process.env.REACT_AUTH_DEBUG && console.log) {
      console.log('withRefreshLock: regular callback');
    }
    return callback();
  }
};
// navigator.locks ? navigator.locks.request('cemit-token-refresh', callback) : callback();

// Makes every hook reading the token re-read it from local storage, without writing it,
// so this tab follows a token another tab stored, or a logout there
export const followStoredToken = () => window.dispatchEvent(new Event('local-storage'));

/**
 * Keeps the login token fresh while the app is open (see tokenRefreshUtils.ts). A new
 * token replaces the stored one, which the API requests read on each call and which
 * other tabs pick up from local storage. When the auth API refuses the token, the user
 * is logged out, so the login page is shown.
 * @param token The current token
 * @param setToken Stores a new token, or clears it with undefined
 * @returns For the login check to exchange a token first; undefined when refreshing is off
 */
export const useTokenRefresh = (
  token: TokenType,
  setToken: SetTokenType,
): TokenRefresher | undefined => {
  // The latest navigate, which changes with the location, for a logout from the scheduler
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const authApiUrl = getCemitAuthApiUrl();
  const isEnabled = isTokenRefreshEnabled() && !!authApiUrl;
  if (process.env.REACT_AUTH_DEBUG && console.log) {
    console.log('useTokenRefresh', {isTokenRefreshEnabled: isEnabled});
  }

  // Checks, refreshes and stores within one lock, so the tabs sharing the token renew it
  // once, and only while it is still the stored token
  const requestRefresh = useMemo(() => {
    if (isEnabled) {
      if (process.env.REACT_AUTH_DEBUG && console.log) {
        console.log('useTokenRefresh: create requestRefresh function');
      }
      return createStoredTokenRefresh({
        read: () => getStoredTokenOrUndefined('useTokenRefresh').raw,
        request: (current) => requestTokenRefresh(authApiUrl, current),
        store: (refreshed, sessionExpiresIn) => {
          saveTokenTimes(refreshed, sessionExpiresIn);
          if (process.env.REACT_AUTH_DEBUG && console.log) {
            console.log('useTokenRefresh.store refreshed');
          }
          setToken((prev) => {
            return {
              raw: refreshed,
              context: 'refresh',
              contextLog: mergeContextLog(prev.contextLog, 'refresh'),
            };
          });
        },
        adopt: followStoredToken,
        lock: withRefreshLock,
      });
    }
    return undefined;
  }, [isEnabled, authApiUrl, setToken]);

  // The scheduler of the current token
  const schedulerRef = useRef<ReturnType<typeof scheduleTokenRefresh>>();
  const refreshNow = useCallback(() => schedulerRef.current?.refreshNow(), []);

  useEffect(() => {
    if (!token.raw || !requestRefresh) {
      return;
    }

    if (process.env.REACT_AUTH_DEBUG && console.log) {
      console.log('useTokenRefresh.useEffect scheduleTokenRefresh()');
    }
    const scheduler = scheduleTokenRefresh(token.raw, {
      requestRefresh,
      onRefused: () => {
        // Log out only if the refused token is still the one in use
        if (
          getStoredTokenOrUndefined('useTokenRefresh.useEffect.scheduler').raw ===
          token.raw
        ) {
          if (process.env.REACT_AUTH_DEBUG && console.log) {
            console.log('useTokenRefresh.logout: token refresh was refused');
          }
          logout(setToken, navigateRef.current);
        } else {
          if (process.env.REACT_AUTH_DEBUG && console.log) {
            console.log('useTokenRefresh.followStoredToken');
          }
          followStoredToken();
        }
      },
      now: () => Date.now(),
      clockOffsetMs: readClockOffsetMs,
      sessionEndsAt: readSessionEndsAt,
      random: () => Math.random(),
      setTimer: (callback, delayMs) => window.setTimeout(callback, delayMs),
      clearTimer: (timer) => window.clearTimeout(timer as number),
    });

    // Catches up after sleep; when the network returns, a pending retry runs at once
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        scheduler.catchUp();
      }
    };

    const onOnline = () => {
      scheduler.catchUp();
      scheduler.retryNow();
    };
    schedulerRef.current = scheduler;

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', onOnline);

    const interval = window.setInterval(scheduler.catchUp, CATCH_UP_INTERVAL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', onOnline);
      window.clearInterval(interval);
      scheduler.stop();
      schedulerRef.current = undefined;
    };
  }, [token, requestRefresh]);

  return useMemo(
    () => (requestRefresh ? {requestRefresh, refreshNow} : undefined),
    [requestRefresh, refreshNow],
  );
};
